import { useEffect, useMemo, useState } from "react"
import { FormGroup, Label, Button, Row, Col } from "reactstrap"
import CustomerSearch from "./CustomerSearch"
import QuotationCustomerForm from "./QuotationCustomerForm"
import { useCustomerInfo } from "hooks/useCustomer"
import { ProfileCard } from "pages/CustomerPages/CustomerProfile/Profile"
import DynamicField from "components/Common/DynamicField"

const CustomerSelector = ({
  apiSearchFunction,
  formik,
  isSearching,
  selectedCustomer,
  setSelectedCustomer,
  existCustomerData,
}) => {
  const [showCustomerProfile, setShowCustomerProfile] = useState(false)
  const [showCustomerForm, setShowCustomerForm] = useState(false)

  const onChange = formik.handleChange
  const error = formik.errors.customer_info
  const touched = formik.touched.customer_info

  // customer info
  const { getQuotationCustomerRichText, getCustomerInfo } = useCustomerInfo()

  const handleCustomerSelect = customer => {
    setSelectedCustomer(customer)

    const customerRichText = getQuotationCustomerRichText(customer)

    onChange({
      target: {
        value: customerRichText,
        name: "customer_info",
      },
    })
    onChange({
      target: {
        value: customer?.name ?? "",
        name: "customer_signatory_name",
      },
    })
  }

  // if existCustomerData is provided, set it as selectedCustomer
  useEffect(() => {
    if (existCustomerData) {
      handleCustomerSelect(existCustomerData)
    }
  }, [existCustomerData])

  const clearSelection = () => {
    setSelectedCustomer(null)
    onChange({ target: { value: "", name: "customer_info" } })
  }

  const currentCustomerInfo = useMemo(
    () => getCustomerInfo(selectedCustomer),
    [selectedCustomer]
  )

  const initialData = {
    ...selectedCustomer,
    name: "Tanvir Ahammed",
    mobile: "01303766837",
    email: "tanvir8321@gmail.com",
    designation_id: "Sr. Software Engineer",
    department_id: "Core Product Development",
    company_id: "TelliCode",
    customer_type_id: "Engineer",
    area_id: "Kuril",
    city_id: "Dhaka",
    full_address: "Ka-80/2, Kuril, Dhaka-1229",
  }

  const onCustomer = (customerInfo, initialData) => {
    handleCustomerSelect({ ...initialData, ...customerInfo })
  }

  return (
    <FormGroup>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Label className="fw-bold mb-0">
          To:
          <span className="text-danger"> *</span>
        </Label>

        {selectedCustomer && (
          <div className="d-flex flex-wrap align-items-center">
            <Button
              color="outline-danger me-2"
              size="sm"
              onClick={clearSelection}
            >
              <i className="bx bx-x"></i> Clear
            </Button>
            <Button
              color="outline-success me-2"
              size="sm"
              onClick={() => handleCustomerSelect(selectedCustomer)}
            >
              <i className="fas fa-user-tag"></i> Refill
            </Button>
            <Button
              color="outline-primary"
              size="sm"
              onClick={() => setShowCustomerProfile(prev => !prev)}
            >
              <i
                className={`fas fa-eye${showCustomerProfile ? "-slash" : ""}`}
              ></i>{" "}
              Profile
            </Button>
          </div>
        )}
      </div>

      {selectedCustomer?.cuid && (
        <div className="quotation-process-card text-center p-4 mb-3 rounded-3 shadow-sm border position-relative">
          <div className="d-flex justify-content-center align-items-center mb-3">
            <i
              className="bx bx-user text-success"
              style={{ fontSize: "2rem" }}
            ></i>
          </div>

          <h5 className="fw-bold text-dark mb-2">
            You have selected this customer for quotation
          </h5>

          <p className="text-muted mb-3">
            Customer ID:{" "}
            <span className="fw-semibold text-success">
              {selectedCustomer?.cuid ?? "-"}
            </span>
            , Name:{" "}
            <span className="fw-semibold text-success">
              {selectedCustomer?.name ?? "-"}
            </span>
          </p>

          <div className="small text-secondary fst-italic">
            Our system is now preparing the quotation based on your customer
            selection.
            <br /> Please review the details before submitting.
          </div>

          <div className="position-absolute top-0 start-0 w-100 h-100 quote-overlay"></div>
        </div>
      )}

      {!selectedCustomer ? (
        <div className="customer-selector">
          <Row className="g-2">
            <Col xs={12} md={8}>
              <CustomerSearch
                onCustomerSelect={handleCustomerSelect}
                apiSearchFunction={apiSearchFunction}
                placeholder="Search existing customers..."
                isSearching={isSearching}
              />
            </Col>
            <Col xs={12} md={4}>
              <Button
                color="primary"
                className="w-100"
                onClick={() => setShowCustomerForm(true)}
              >
                <i className="bx bx-user-plus me-1"></i>
                New Customer
              </Button>
            </Col>
          </Row>
        </div>
      ) : (
        <>
          <FormGroup>
            <div className="position-relative">
              <DynamicField
                type="richtext"
                name="customer_info"
                value={formik.values.customer_info}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                setFieldValue={formik.setFieldValue}
                placeholder="Enter customer details..."
                required
                height={100}
                invalid={
                  formik.touched.customer_info &&
                  Boolean(formik.errors.customer_info)
                }
                feedback={
                  formik.touched.customer_info && formik.errors.customer_info
                }
              />
            </div>
          </FormGroup>
        </>
      )}

      {touched && error && (
        <div className="invalid-feedback d-block">{error}</div>
      )}

      {showCustomerProfile && (
        <ProfileCard
          data={currentCustomerInfo}
          loading={false}
          onCustomer={onCustomer}
        />
      )}

      {/* Customer Form Modal */}
      <QuotationCustomerForm
        isOpen={showCustomerForm}
        toggle={() => setShowCustomerForm(prev => !prev)}
        onCustomer={onCustomer}
        initialData={initialData}
        title={`${initialData?.id ? "Edit New Customer" : "Add New Customer"}`}
        submitText={`${initialData?.id ? "Edit Customer" : "Add Customer"}`}
      />
    </FormGroup>
  )
}

export default CustomerSelector
